// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import client from '@Client';
import { THLayout } from '@Molecules/THLayout';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { ActionOutput, AuditOutput, ObjectTypeV1 as ObjectType, CaseOutput, OrgPermissionsEnum } from 'thehive-sdk';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { useLocalMessage } from '@Helpers/locale';
import ResponderReports from '@Organisms/Report/ResponderReports/ResponderReports';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { ICONS } from '@Constants/iconsApp';
import { ResponderDrawer } from '@Organisms/Responder';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { computeGetSelector } from '@Organisms/Case/selector';
import useOneQuery from '@Hooks/useOneQuery';
import { Space } from 'antd';
import useCasePermissions from '@Hooks/useCasePermissions';

import './Responders.scss';

const actionIsCase = (audit: AuditOutput): boolean => audit.base?.details?.objectType === 'Case';

const CaseResponders = (): ReactElement => {
    const i = useLocalMessage();

    const { caseId } = useParams<{ caseId: string }>();

    const hasPermissions = useCasePermissions({ caseId, permissionsNeeded: [OrgPermissionsEnum.MANAGE_ACTION] });

    const { data: caseData } = useOneQuery<CaseOutput>({
        controller: client.CaseController,
        selector: computeGetSelector({ caseId }),
        fetchOnStart: false,
        manual: true,
    });

    const config = useConfigList<ActionOutput>({
        selector: {
            head: [{ name: 'getCase', idOrName: caseId }, { name: 'actions' }],
            name: `get-report-case-${caseId}`,
            sort: ['-startDate'],
        },
        controller: client.CaseController,
        storageId: StorageFilterEnum.CASE_RESPONDER,
        objectType: ObjectType.ACTION,
        streamRootId: caseId,
        pagination: 'on',
    });

    const { dataSource = [], listRefetch, status = 'loading', columnsRenderer } = config;

    useStreamObserver({ objectType: STREAM_EVENTS.ACTION }, (audits: AuditOutput[]): void => {
        if (audits.some(actionIsCase)) {
            listRefetch();
        }
    });

    return (
        <THLayout>
            <THLayout.Content>
                {dataSource.length ? (
                    <ResponderReports reports={dataSource} columnsRenderer={columnsRenderer} />
                ) : (
                    <DisplayEmptyData
                        spin={status === 'loading'}
                        label={
                            <Space size={2}>
                                {i('responder.no-reports')}
                                {hasPermissions && (
                                    <ResponderDrawer
                                        triggerElem={<THButton type="link"> {i('responder.launch')}</THButton>}
                                        objectId={caseId}
                                        objectType="case"
                                        objectData={{ caseNumber: caseData?.number + '' }}
                                    />
                                )}
                            </Space>
                        }
                    />
                )}
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.responders',
        icon: <ICONS.RespondersIcon />,
        shortcut: {
            enable: true,
        },
    },
    path: RouteBuilder.compute('cases.responders'),
    content: <CaseResponders />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.responders' },
};
